/**
 * Puts the signed-in user's context into a Swagger 2.0 spec as parameter defaults.
 *
 * Swagger UI prefills each Try-it-out field from that parameter's `default`, so this is what makes
 * `companyId` arrive already filled in - 87 times across the Web API spec, against 4 for `userId`.
 * Doing it through the spec rather than by writing to the rendered inputs matters: those inputs are
 * React-controlled, so a value assigned to the DOM node would show up but never reach Swagger UI's
 * own state, and Execute would still send nothing.
 */

/** Where /apidocs declares every spec it publishes - the same list the official page's picker shows. */
export const SPEC_LIST_URL = "/apidocs/swagger-initializer.js";

const APIDOCS_ROOT = "/apidocs/";

/** The default selection: the spec this page was built for. */
export const DEFAULT_SPEC_NAME = "Web API (Visma)";

/**
 * The list as published at the time of writing, used only if the initializer cannot be read or its
 * shape changes - an empty picker would be worse than a slightly stale one.
 */
export const FALLBACK_SPECS = [
    {name: "Task API (Visma)", url: `${APIDOCS_ROOT}specs/task-api.json`},
    {name: "Platform API (Visma)", url: `${APIDOCS_ROOT}specs/platform-api.json`},
    {name: "Platform API (IPP)", url: `${APIDOCS_ROOT}specs/platform-api-ipp.json`},
    {name: "Web API (Visma)", url: `${APIDOCS_ROOT}specs/web-api.json`},
    {name: "Backend API (internal)", url: `${APIDOCS_ROOT}specs/backend-api.json`},
    {name: "Support API (internal)", url: `${APIDOCS_ROOT}specs/support-api.json`},
    {name: "Configuration API (Connect, Visma)", url: `${APIDOCS_ROOT}configurationapi/configuration-v1.yaml`},
    {name: "Documents API (Connect, Visma)", url: `${APIDOCS_ROOT}documents/documents-v1.yaml`},
    {name: "Documents Service API (Visma)", url: `${APIDOCS_ROOT}documents/document-service.openapi.yaml`},
];

// the initializer is JavaScript, not JSON: the keys are unquoted, so it is read rather than parsed
const SPEC_ENTRY = /\{\s*name:\s*"([^"]+)"\s*,\s*url:\s*"([^"]+)"\s*\}/g;

function resolve(url) {
    return /^(https?:)?\/\//.test(url) || url.startsWith("/") ? url : APIDOCS_ROOT + url;
}

/**
 * Reads the published spec list out of /apidocs/swagger-initializer.js, so a spec added to apidocs
 * shows up here without a code change.
 *
 * @param {string} source the initializer's JavaScript
 * @returns {{name: string, url: string}[]} the published specs, or FALLBACK_SPECS if none were found
 */
export function parseSpecList(source) {
    const found = [...String(source ?? "").matchAll(SPEC_ENTRY)]
        .map(([, name, url]) => ({name, url: resolve(url)}));
    return found.length > 0 ? found : FALLBACK_SPECS;
}

/**
 * Whether a spec can be fetched, patched and handed over as an object.
 *
 * Only the JSON specs can: they are Swagger 2.0, where a parameter's `default` is what Swagger UI
 * prefills from. The YAML ones are OpenAPI 3, which puts defaults under `schema` instead, and would
 * need a YAML parser besides - so those are handed to Swagger UI as a URL for it to load itself.
 */
export function isPatchable(spec) {
    return Boolean(spec?.url?.endsWith(".json"));
}

/** Only these parameter names are filled, and only where the URL carries them. */
const FILLED_IN = ["path", "query"];

function withDefault(parameter, context) {
    const value = context?.[parameter.name];
    // a default already in the spec is the API's own answer and outranks ours
    if (value == null || parameter.default !== undefined || !FILLED_IN.includes(parameter.in)) {
        return parameter;
    }
    return {...parameter, default: value};
}

function withDefaults(parameters, context) {
    return Array.isArray(parameters)
        ? parameters.map(parameter => withDefault(parameter, context))
        : parameters;
}

/**
 * @param {object} spec a Swagger 2.0 spec, left untouched
 * @param {{companyId?: number|string, userId?: number|string}} context values to prefill by
 *   parameter name; a nullish value fills nothing, so a half-loaded session cannot inject `null`
 * @returns {object} a copy whose matching parameters carry a `default`
 */
export function withContextDefaults(spec, context) {
    if (spec?.paths == null) {
        return spec;
    }

    const paths = {};
    for (const [path, pathItem] of Object.entries(spec.paths)) {
        const patched = {};
        for (const [key, value] of Object.entries(pathItem)) {
            if (key === "parameters") {
                // 4 of the spec's companyId parameters are declared here, shared by the whole path item
                patched[key] = withDefaults(value, context);
            } else if (value?.parameters != null) {
                patched[key] = {...value, parameters: withDefaults(value.parameters, context)};
            } else {
                patched[key] = value;
            }
        }
        paths[path] = patched;
    }

    return {...spec, paths};
}
